import type { I18nProvider } from '@refinedev/core';
import type { MantineTheme } from '@mantine/core';
import { MRT_ColumnFiltersState } from 'mantine-react-table';
import { TaskStatus } from 'interfaces/task';

export enum UploadStatusGroup {
  all = 'all',
  incomplete = 'incomplete',
  complete = 'complete',
}

export type UploadStatusGroupCounts = Record<UploadStatusGroup, number>;

const ALL_TASK_STATUSES = Object.values(TaskStatus);
const taskStatusSet: ReadonlySet<string> = new Set(ALL_TASK_STATUSES);

export const UPLOAD_DEFAULT_STATUS_GROUP = UploadStatusGroup.incomplete;

export const uploadStatusGroups: Record<UploadStatusGroup, TaskStatus[]> = {
  [UploadStatusGroup.all]: ALL_TASK_STATUSES,
  [UploadStatusGroup.incomplete]: [
    TaskStatus.toDo,
    TaskStatus.inProgress,
    TaskStatus.revisionRequired,
    TaskStatus.autoComplete,
  ],
  [UploadStatusGroup.complete]: [TaskStatus.complete, TaskStatus.closed],
};

export const isUploadStatusesCountResult = (
  value: unknown
): value is Record<string, number> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const getUploadStatusGroupCounts = (
  taskStatusCounts?: Record<string, number> | null
): UploadStatusGroupCounts =>
  Object.fromEntries(
    (Object.values(UploadStatusGroup) as UploadStatusGroup[]).map((group) => [
      group,
      uploadStatusGroups[group].reduce(
        (sum, statusKey) => sum + (taskStatusCounts?.[statusKey] ?? 0),
        0
      ),
    ])
  ) as UploadStatusGroupCounts;

export const uploadStatusColors: Record<TaskStatus, string> = {
  error: '#C62828',
  inProgress: '#2F6690',
  readyForReview: '#A65A00',
  revisionRequired: '#5C6BC0',
  autoComplete: '#c4bd03',
  complete: '#2E7D32',
  toDo: 'gray',
  closed: '#6A4C93',
};

export const isUploadStatus = (
  value: string | null | undefined
): value is TaskStatus => typeof value === 'string' && taskStatusSet.has(value);

const getStatusTranslationKey = (statusKey: string) =>
  `pages:uploads.statuses.${statusKey}`;

export const formatUploadStatusLabel = (
  statusKey: string,
  translate?: I18nProvider['translate']
) => {
  if (!translate) return statusKey;
  return translate(getStatusTranslationKey(statusKey), statusKey);
};

export const getUploadStatusOptions = (translate?: I18nProvider['translate']) =>
  ALL_TASK_STATUSES.map((value) => ({
    value,
    label: formatUploadStatusLabel(value, translate),
  }));

const uniqueSorted = (values: string[]) => Array.from(new Set(values)).sort();

const statusSetsEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

export const getUploadStatusFilterValues = (
  columnFilters: MRT_ColumnFiltersState
): string[] =>
  uniqueSorted(
    (columnFilters ?? [])
      .filter((filter) => filter.id === 'status')
      .flatMap((filter) =>
        Array.isArray(filter.value)
          ? filter.value.map(String)
          : [String(filter.value)]
      )
      .filter(Boolean)
  );

export const getActiveUploadStatusGroup = (
  columnFilters: MRT_ColumnFiltersState
): UploadStatusGroup | null => {
  const selected = getUploadStatusFilterValues(columnFilters);
  if (!selected.length) return UploadStatusGroup.all;

  const incomplete = uniqueSorted(uploadStatusGroups[UploadStatusGroup.incomplete]);
  if (statusSetsEqual(selected, incomplete)) return UploadStatusGroup.incomplete;

  const complete = uniqueSorted(uploadStatusGroups[UploadStatusGroup.complete]);
  if (statusSetsEqual(selected, complete)) return UploadStatusGroup.complete;

  const allKnown = uniqueSorted(uploadStatusGroups[UploadStatusGroup.all]);
  if (statusSetsEqual(selected, allKnown)) return UploadStatusGroup.all;

  return null;
};

export const applyUploadStatusGroup = (
  currentFilters: MRT_ColumnFiltersState,
  group: UploadStatusGroup
): MRT_ColumnFiltersState => {
  const nonStatusFilters = (currentFilters ?? []).filter(
    (filter) => filter.id !== 'status'
  );

  if (group === UploadStatusGroup.all) return nonStatusFilters;

  return [
    ...nonStatusFilters,
    {
      id: 'status',
      value: [...uploadStatusGroups[group]],
    },
  ];
};

export const getUploadStatusBadgeProps = (
  statusKey: string,
  theme: MantineTheme
) => {
  const normalizedStatus = isUploadStatus(statusKey) ? statusKey : null;
  const isTodo = normalizedStatus === TaskStatus.toDo;
  if (isTodo || !normalizedStatus) {
    return {
      color: 'gray',
      variant: 'outline' as const,
      style: {
        backgroundColor: theme.white,
        borderColor: theme.colors.gray[3],
        color: theme.colors.dark[7],
      },
    };
  }

  let textColor = theme.white;
  // For colours that use a brighter badge fill,
  // force dark text for contrast.
  if (normalizedStatus === TaskStatus.autoComplete) {
    textColor = theme.colors.dark[9];
  }

  return {
    color: uploadStatusColors[normalizedStatus],
    variant: 'filled' as const,
    style: {
      color: textColor,
    },
  };
};
